/**
 * User-friendly error messages mapped to backend error codes
 * Falls back to backend message if code not found
 */

import { ErrorCodes } from '../constants/errorCodes';
import { CLIENT_ERROR_CODES } from '../constants/clientErrorCodes';

/**
 * Map of backend error codes to user-friendly messages
 * These messages are shown to end users in the UI
 * 
 * Uses ErrorCodes constants for type safety
 */
export const ERROR_MESSAGES: Record<string, string> = {
  // Authentication errors (AUTH_0xx)
  [ErrorCodes.AUTH_UNAUTHENTICATED]: 'Your session has expired. Please log in again.',
  [ErrorCodes.AUTH_UNAUTHORIZED]: 'You do not have permission to access this resource.',
  [ErrorCodes.AUTH_INVALID_CREDENTIALS]: 'Invalid email or password. Please try again.',
  [ErrorCodes.AUTH_TOKEN_EXPIRED]: 'Your session has expired. Please log in again.',
  [ErrorCodes.AUTH_SESSION_INVALID]: 'Your session is invalid. Please log in again.',
  [ErrorCodes.AUTH_MISSING_CREDENTIALS]: 'Please provide your credentials to continue.',
  [ErrorCodes.AUTH_INVALID_KEY]: 'The provided authentication key is invalid.',
  [ErrorCodes.AUTH_MISSING_KEY]: 'Authentication key is missing.',
  [ErrorCodes.AUTH_INVITATION_LOCKED]: 'This invitation has been locked due to four unsuccessful validation attempts.',
  [ErrorCodes.AUTH_VALIDATION_IN_PROGRESS]: 'Another sign-in attempt for this invitation is still being checked. Please wait a moment and try again.',
  [ErrorCodes.AUTH_CHALLENGE_FAILED]: "We couldn't complete the security check. Please try again.",

  // Validation errors (VAL_0xx)
  [ErrorCodes.VAL_REQUIRED_FIELD]: 'This field is required.',
  [ErrorCodes.VAL_INVALID_FORMAT]: 'The format of this field is invalid.',
  [ErrorCodes.VAL_OUT_OF_RANGE]: 'The value is out of the allowed range.',
  [ErrorCodes.VAL_DUPLICATE_VALUE]: 'This value already exists.',

  // Business logic errors (BUS_0xx)
  // Named for a locked application form, but every emit site is a readonly file field.
  [ErrorCodes.BUS_APPLICATION_LOCKED]: 'This file cannot be changed.',
  [ErrorCodes.BUS_SECTION_INCOMPLETE]: 'This section still has required information missing.',
  [ErrorCodes.BUS_ALREADY_SUBMITTED]: 'This item has already been submitted.',
  [ErrorCodes.BUS_NOT_FOUND]: 'The requested resource was not found.',
  [ErrorCodes.BUS_SIGNATURE_REQUIRED]: 'This application needs to be signed before it can be submitted.',
  [ErrorCodes.BUS_OPERATION_FAILED]: 'The operation could not be completed.',
  [ErrorCodes.BUS_INVALID_OPERATION]: 'This operation is not allowed.',
  [ErrorCodes.BUS_CONFLICT]: 'This operation conflicts with existing data.',

  // Server errors (SRV_0xx)
  [ErrorCodes.SRV_INTERNAL_ERROR]: 'An unexpected error occurred. Please try again later.',
  [ErrorCodes.SRV_DATABASE_ERROR]: 'A database error occurred. Please contact support.',
  [ErrorCodes.SRV_EXTERNAL_SERVICE_ERROR]: 'A connected service is not responding. Please try again later.',
  [ErrorCodes.SRV_SERVICE_UNAVAILABLE]: 'The service is temporarily unavailable. Please try again later.',

  // Request errors (REQ_0xx) - client-side
  [CLIENT_ERROR_CODES.BAD_REQUEST]: 'The request was rejected. Please check your entries and try again.',

  // Network errors (NET_0xx) - client-side
  [CLIENT_ERROR_CODES.NETWORK_TIMEOUT]: 'The request timed out. Please check your connection and try again.',
  [CLIENT_ERROR_CODES.NETWORK_OFFLINE]: 'You appear to be offline. Please check your internet connection.',
  [CLIENT_ERROR_CODES.NETWORK_CONNECTION_REFUSED]: 'Could not connect to the server. Please try again.',
  [CLIENT_ERROR_CODES.NETWORK_ABORTED]: 'The request was cancelled.',

  // Unknown errors
  [CLIENT_ERROR_CODES.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again.',
  [CLIENT_ERROR_CODES.PARSING_ERROR]: 'Failed to parse the server response.',
};

/**
 * Gets a user-friendly error message for a given error code
 * Falls back to the provided backend message if no mapping exists
 * 
 * @param code - Backend error code (e.g., 'AUTH_001')
 * @param fallbackMessage - Message from backend to use if no mapping found
 * @returns User-friendly error message
 */
export function getErrorMessage(
  code: string,
  fallbackMessage?: string,
  options?: { fieldScoped?: boolean },
): string {
  // A field-scoped error names the exact field and limit that failed ("Maximum of
  // 1 file(s) allowed"); the catalogue entry for its code is deliberately generic,
  // so preferring it here would throw the useful part away.
  if (options?.fieldScoped && fallbackMessage) {
    return fallbackMessage;
  }

  return ERROR_MESSAGES[code] || fallbackMessage || ERROR_MESSAGES.UNK_001;
}

/**
 * Gets a user-friendly title for error categories
 * Used for error dialog/notification titles
 * 
 * @param category - Error category
 * @returns Human-readable category title
 */
export function getErrorCategoryTitle(
  category: 'auth' | 'validation' | 'business' | 'network' | 'server'
): string {
  switch (category) {
    case 'auth':
      return 'Authentication Error';
    case 'validation':
      return 'Validation Error';
    case 'business':
      return 'Operation Failed';
    case 'network':
      return 'Connection Error';
    case 'server':
      return 'Server Error';
    default:
      return 'Error';
  }
}

/**
 * Determines if an error should be shown to the user
 * Some errors (like network timeouts during retry) might be handled silently
 * 
 * @param _code - Error code (not currently used, but reserved for future logic)
 * @returns True if error should be displayed to user
 */
export function shouldDisplayError(_code: string): boolean {
  // Currently show all errors, but this can be customized
  // For example, you might want to silently handle certain network errors
  return true;
}

/**
 * Gets an action label for an error (e.g., "Try Again", "Log In")
 * Used for error action buttons
 * 
 * @param code - Error code
 * @returns Action label
 */
export function getErrorActionLabel(code: string): string {
  if (
    code === ErrorCodes.AUTH_UNAUTHENTICATED ||
    code === ErrorCodes.AUTH_TOKEN_EXPIRED ||
    code === ErrorCodes.AUTH_SESSION_INVALID
  ) {
    return 'Log In';
  }

  if (code.startsWith('NET_')) {
    return 'Retry';
  }

  return 'OK';
}
